import React from 'react';
import { ShoppingCart, User, CreditCard, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface CartProgressIndicatorProps {
  currentStep: 'cart' | 'info' | 'payment';
}

export const CartProgressIndicator: React.FC<CartProgressIndicatorProps> = ({ currentStep }) => {
  const steps = [
    { key: 'cart', label: 'Carrito', icon: ShoppingCart },
    { key: 'info', label: 'Datos', icon: User },
    { key: 'payment', label: 'Pago', icon: CreditCard },
  ];

  const getCurrentStepIndex = () => {
    return steps.findIndex(step => step.key === currentStep);
  };

  const currentIndex = getCurrentStepIndex();

  return (
    <div className="relative w-full mb-8">
      {/* Background Progress Line */}
      <div className="absolute top-6 left-0 w-full h-0.5 bg-gradient-to-r from-muted via-muted to-muted" />
      <motion.div 
        className="absolute top-6 left-0 h-0.5 bg-gradient-to-r from-primary via-primary to-green-500 rounded-full"
        initial={{ width: "0%" }}
        animate={{ 
          width: currentIndex === 0 ? "0%" : currentIndex === 1 ? "50%" : "100%" 
        }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
      />
      
      {/* Steps */}
      <div className="relative flex items-center justify-between w-full">
        {steps.map((step, index) => {
          const isActive = step.key === currentStep;
          const isCompleted = index < currentIndex;
          const isUpcoming = index > currentIndex;
          
          return (
            <motion.div 
              key={step.key} 
              className="flex flex-col items-center space-y-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <motion.div
                className={cn(
                  "relative flex items-center justify-center w-12 h-12 rounded-full border-2 backdrop-blur-sm transition-all duration-500 shadow-lg",
                  isActive && "bg-gradient-to-br from-primary to-primary/80 border-primary text-primary-foreground shadow-primary/25",
                  isCompleted && "bg-gradient-to-br from-green-500 to-green-600 border-green-500 text-white shadow-green-500/25",
                  isUpcoming && "bg-background/80 border-muted-foreground/20 text-muted-foreground shadow-sm"
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                layout
              >
                <AnimatePresence mode="wait">
                  {isCompleted ? (
                    <motion.div
                      key="check"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0, rotate: 180 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Check className="h-5 w-5" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="icon"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <step.icon className="h-5 w-5" />
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Active Step Pulse Effect */}
                {isActive && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-primary/20"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                )}
              </motion.div>
              
              <motion.span
                className={cn(
                  "text-sm font-medium transition-all duration-300 text-center",
                  isActive && "text-primary font-semibold",
                  isCompleted && "text-green-600 font-medium",
                  isUpcoming && "text-muted-foreground"
                )}
                animate={{
                  scale: isActive ? 1.05 : 1,
                  fontWeight: isActive ? 600 : 500
                }}
                transition={{ duration: 0.2 }}
              >
                {step.label}
              </motion.span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};